import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAppCtx } from '../context/AppCtx';

const PAGE_META: Record<string, { title: string; subtitle: string }> = {
  '/upload':              { title: 'Importação de Dados',      subtitle: 'Carregue os arquivos para iniciar a análise' },
  '/dashboard':           { title: 'Dashboard CEO',             subtitle: 'Visão consolidada de compras, ruptura e saúde financeira' },
  '/comprador':           { title: 'Painel do Comprador',       subtitle: 'Sugestões de compra, margem e negociação' },
  '/financeiro':          { title: 'Validação Financeira',      subtitle: 'Aprovações, prazos, boletos e exposição' },
  '/cronograma':          { title: 'Cronograma de Pagamentos',  subtitle: 'Visão semanal — pedidos × Google Sheets × capacidade real' },
  '/logistica':           { title: 'Logística & Recebimento',   subtitle: 'Agendamento, entregas e lead time' },
  '/conferencia-nf':      { title: 'Conferência NF × Pedido',  subtitle: 'Anexar Nota Fiscal e confrontar com o pedido aprovado' },
  '/controle-pedidos':    { title: 'Controle de Pedidos',       subtitle: 'Cumprimento, divergências, lead time, bonificação e diferença pedido × NF por fornecedor' },
  '/historico':           { title: 'Histórico & Comparativos',  subtitle: 'Snapshots diários e evolução' },
  '/fornecedor':          { title: 'Análise de Fornecedores',   subtitle: 'Performance, atrasos e rupturas' },
  '/conclusao-executiva': { title: 'Conclusão Executiva',       subtitle: 'Diagnóstico e recomendações automáticas' },
};

const NAV_IMPORT = [
  { to: '/upload',     icon: '📥', label: 'Importação' },
];

const NAV_OP = [
  { to: '/dashboard',      icon: '🏛️', label: 'Dashboard CEO' },
  { to: '/comprador',      icon: '🛒', label: 'Comprador' },
  { to: '/financeiro',     icon: '💳', label: 'Financeiro' },
  { to: '/cronograma',     icon: '🗓️', label: 'Cronograma' },
  { to: '/logistica',      icon: '🚚', label: 'Logística' },
  { to: '/conferencia-nf',   icon: '📑', label: 'Conferência NF' },
  { to: '/controle-pedidos', icon: '📊', label: 'Controle de Pedidos' },
  { to: '/fornecedor',       icon: '🏭', label: 'Fornecedor' },
];

const NAV_INTEL = [
  { to: '/conclusao-executiva', icon: '🧠', label: 'Conclusão Executiva' },
  { to: '/historico',           icon: '🗂️', label: 'Histórico' },
];

const ROLE_ROUTES: Record<string, string> = {
  ceo:        '/dashboard',
  comprador:  '/comprador',
  financeiro: '/financeiro',
  logistica:  '/logistica',
};

function SideNavLink({ to, icon, label }: { to: string; icon: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `nav-item flex items-center gap-3 px-5 py-2.5 cursor-pointer text-sm text-mbc-soft${isActive ? ' active' : ''}`
      }
    >
      <span>{icon}</span>
      <span>{label}</span>
    </NavLink>
  );
}

export default function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const ctx = useAppCtx();
  const meta = PAGE_META[pathname] ?? { title: 'MBC Supply', subtitle: '' };

  const [role, setRole] = useState(() => localStorage.getItem('mbc_role') || 'ceo');
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  function handleRoleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const v = e.target.value;
    setRole(v);
    localStorage.setItem('mbc_role', v);
    if (ctx.dataStamp) navigate(ROLE_ROUTES[v] || '/dashboard');
  }

  const stampLines = ctx.dataStamp
    ? [`Processado ${ctx.dataStamp.toLocaleString('pt-BR')}`, `Horizonte ${ctx.horizon}d`]
    : null;

  return (
    <div className="flex min-h-screen">

      {navOpen && (
        <button
          type="button"
          aria-label="Fechar menu"
          className="fixed inset-0 z-20 bg-black/55 md:hidden"
          onClick={() => setNavOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside className={`w-72 max-w-[86vw] shrink-0 bg-mbc-ink border-r border-mbc-line flex flex-col fixed h-screen z-30 transition-transform duration-200 md:w-60 ${navOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}>

        {/* Logo */}
        <div className="px-5 py-5 border-b border-mbc-line">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-mbc-blue to-mbc-orange grid place-items-center font-black text-white text-lg shadow-lg">
              M
            </div>
            <div>
              <div className="text-white font-extrabold tracking-tight leading-none text-lg">MBC</div>
              <div className="text-[10px] text-mbc-mute tracking-widest uppercase">Supply · v6.8 desacopla</div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 overflow-y-auto text-sm">
          {NAV_IMPORT.map(n => <SideNavLink key={n.to} {...n} />)}

          <div className="px-5 pt-4 pb-1 text-[10px] uppercase tracking-widest text-mbc-mute/70">
            Operação
          </div>
          {NAV_OP.map(n => <SideNavLink key={n.to} {...n} />)}

          <div className="px-5 pt-4 pb-1 text-[10px] uppercase tracking-widest text-mbc-mute/70">
            Inteligência
          </div>
          {NAV_INTEL.map(n => <SideNavLink key={n.to} {...n} />)}
        </nav>

        {/* User / role / logout */}
        <div className="px-5 py-3 border-t border-mbc-line text-[11px] text-mbc-mute">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-mbc-blue/40 grid place-items-center text-white text-xs font-bold shrink-0">
              MC
            </span>
            <div className="flex-1 leading-tight min-w-0">
              <div className="text-mbc-soft font-semibold">MBC Ferramentas</div>
              <select
                value={role}
                onChange={handleRoleChange}
                className="bg-transparent text-mbc-mute text-[11px] -ml-0.5 w-full"
              >
                <option value="ceo">CEO / Diretoria</option>
                <option value="comprador">Comprador</option>
                <option value="financeiro">Financeiro</option>
                <option value="logistica">Logística</option>
              </select>
            </div>
            <button
              onClick={() => logout()}
              className="text-rose-300 hover:text-rose-200 text-[10px] hover:underline shrink-0"
            >
              sair
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="flex-1 min-w-0 md:ml-60">

        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-mbc-ink/85 backdrop-blur-sm border-b border-mbc-line px-4 py-3 md:px-7 flex items-center gap-3 md:gap-4">
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            className="grid h-10 w-10 place-items-center rounded-lg border border-mbc-line bg-mbc-panel2 text-mbc-soft md:hidden"
            aria-label="Abrir menu"
          >
            ☰
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-white font-bold text-lg leading-none truncate">{meta.title}</h1>
            {meta.subtitle && (
              <p className="text-xs text-mbc-mute mt-1 hidden sm:block">{meta.subtitle}</p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2 md:gap-3 flex-wrap justify-end">
            <div className="hidden md:flex items-center gap-2 text-xs">
              <span className="text-mbc-mute">Projeção</span>
              <select
                value={ctx.horizon}
                onChange={e => ctx.setHorizon(+e.target.value)}
                className="bg-mbc-panel2 border border-mbc-line rounded-lg px-2 py-1.5 text-mbc-soft"
              >
                {[45, 30, 60, 90].map(d => (
                  <option key={d} value={d}>{d} dias</option>
                ))}
              </select>
            </div>
            {stampLines && (
              <div className="hidden lg:block text-right text-[11px] text-mbc-mute leading-tight">
                {stampLines.map((l, i) => <div key={i}>{l}</div>)}
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 md:p-7">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
